// A manager's lineup for one week as plain text, for the share sheet.
//
// Written in the third person: the Scoreboard shares anyone's card, so the
// text names the team and never says "my". It is also the fallback for the
// places a picture cannot go (a browser with no file sharing, an app that
// drops the caption), so it has to stand on its own.
//
// Points and game state are worked out exactly as ReadOnlyTeamRow does for
// the Scoreboard, from the same helpers, so a shared lineup cannot disagree
// with the card it was shared from.
import { calculateCombinedPoints, getGameDisplayInfo, getGameSpreadDisplay, isSuperDog } from "./gameStatus";
import { getWeeklyPoints } from "./teamStats";
import { SUPER_DOG_MARK } from "./superDog";
import { ordinal } from "./leagueStatus";

export const SHARE_SITE = "lineupcfb.com";

const pts = (n) => (Math.abs(n) === 1 ? "pt" : "pts");

/**
 * One starter as the share needs it: what state its game is in and what it
 * has scored. Exported for the picture card, which draws the same rows.
 */
export function describeStarter(slug, { week, captain, tripPlayTeam, frozenTeams = [], teamPoints = {}, allTeams, gamesBySlug, weeklyPointsBySlug }) {
  const team = allTeams?.[slug];
  const game = gamesBySlug?.[slug];
  const info = getGameDisplayInfo(game, slug, allTeams);
  const isCaptain = captain === slug;
  const isTripPlay = tripPlayTeam === slug;
  const isFrozen = frozenTeams.includes(slug) && typeof teamPoints[slug] === "number";

  // An interrupted game reports whether it had kicked off, since its type
  // alone cannot say. See ReadOnlyTeamRow.
  const started = info.type !== "upcoming" && info.type !== "bye-week" && info.hasStarted !== false;
  const base = weeklyPointsBySlug?.[slug]
    ? getWeeklyPoints({ weeklyPoints: weeklyPointsBySlug[slug] }, week)
    : getWeeklyPoints(team?.currentSeason, week);

  let points = null;
  if (isFrozen) points = teamPoints[slug];
  else if (started) points = calculateCombinedPoints(base, isCaptain, isTripPlay).finalPoints;

  return {
    slug,
    name: team?.school || slug,
    isCaptain,
    isTripPlay,
    isFrozen,
    isBye: info.type === "bye-week",
    isLive: Boolean(info.isLive),
    isDone: info.type === "completed",
    started,
    points,
    spread: getGameSpreadDisplay(game, slug),
    superDog: isSuperDog(game, slug),
  };
}

/**
 * Where the week stands for this lineup: "pre" before any starter has kicked
 * off, "final" once every starter with a game has finished, "live" between.
 */
export function lineupShareState(rows) {
  const playing = rows.filter((r) => !r.isBye);
  if (!playing.some((r) => r.started || r.isFrozen)) return "pre";
  return playing.every((r) => r.isDone) ? "final" : "live";
}

function rowText(row) {
  // The crown rides after the name, in brackets, rather than taking the
  // leading mark: that slot says what state the game is in, and the captain
  // has a state like any other row.
  const crown = row.isCaptain ? (row.isTripPlay ? "👑⚡" : "👑") : "";
  const team = `${row.name}${crown ? ` (${crown})` : ""}`;
  if (row.isBye) return `💤 ${team} (bye)`;

  // One number per row, to keep a row on one line of a chat bubble: the
  // spread until the game kicks off, the points from then on. They are told
  // apart by form, since a lineup shared mid Saturday has both kinds of row:
  // a spread is always in brackets, a score always says "pts".
  if (row.points === null) {
    // The captain's crown shares the spread's bracket rather than opening a
    // second one beside it.
    const inside = [crown, row.spread].filter(Boolean).join(" ");
    return `🔜 ${row.name}${inside ? ` (${inside})` : ""}${row.superDog ? ` ${SUPER_DOG_MARK}` : ""}`;
  }

  // A stopwatch leads a row whose game is in play, in place of the colour:
  // red or green is a verdict, and these points can still move. A frozen
  // team's cannot, whatever its game is doing, so it keeps the colour.
  const inPlay = row.isLive && !row.isFrozen;
  const mark = inPlay ? "⏱️" : row.points > 0 ? "🟩" : row.points < 0 ? "🟥" : "🟨";
  return `${mark} ${team} ${row.points} ${pts(row.points)}${row.isFrozen ? " ❄️ frozen" : ""}`;
}

/**
 * @param {object} lineup
 * @param {string} lineup.teamName the manager's squad name
 * @param {string} [lineup.managerName] the manager's first name
 * @param {number|string} lineup.week
 * @param {number|null} lineup.points the week's total as the scorer wrote it
 * @param {string[]} lineup.starters team slugs, empty slots already removed
 * @param {number} [lineup.weekRank] 1-based rank on the week's points
 * @param {number} [lineup.managerCount]
 */
export function buildLineupShareText(lineup) {
  const { teamName, managerName, week, points, starters = [], weekRank, managerCount } = lineup;
  // The captain leads whichever slot it sits in: it is the pick of the week
  // and the row a reader looks for first. The rest keep their lineup order.
  const slugs = starters.filter(Boolean);
  const ordered = [...slugs.filter((s) => s === lineup.captain), ...slugs.filter((s) => s !== lineup.captain)];
  const rows = ordered.map((slug) => describeStarter(slug, lineup));
  const state = lineupShareState(rows);

  // First name only, and on every share, the sharer's own included: a group
  // chat knows its people by name, not by squad name, and a forwarded message
  // no longer says who sent it.
  const who = managerName?.trim() ? `${teamName} (${managerName.trim()})` : teamName;
  const lines = [`🏈 ${who} · Week ${week}${state === "pre" ? " lineup" : ""}`];

  if (state !== "pre") {
    const total = `${points ?? 0} ${pts(points ?? 0)}${state === "live" ? " (live)" : ""}`;
    const rank = weekRank && managerCount ? ` · ${ordinal(weekRank)} of ${managerCount}${state === "final" ? " this week" : ""}` : "";
    lines.push(`${total}${rank}`);
  }

  const tripPlay = rows.find((r) => r.isTripPlay);
  if (tripPlay) lines.push(`⚡ 5x Play on ${tripPlay.name}`);

  lines.push("");
  if (rows.length === 0) lines.push("No starters set.");
  rows.forEach((row) => lines.push(rowText(row)));
  lines.push("", SHARE_SITE);

  return lines.join("\n");
}

/**
 * 1-based rank of one member on the week's points, ties sharing the higher
 * place, from every manager's { memberId, points } for that week.
 */
export function weekRankOf(memberId, weekTotals) {
  const mine = weekTotals.find((t) => t.memberId === memberId);
  if (!mine) return null;
  return 1 + weekTotals.filter((t) => (t.points ?? 0) > (mine.points ?? 0)).length;
}
